import dayjs, { Dayjs } from "dayjs";
import { useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { AnyForNow } from "../features/grn/types";
import { GRN_ERROR_MESSAGES } from "../utils/sys/GrnValidation";

// Interface for initial data parameter
interface GrnDetailsInitialData {
  warehouse?: string;
  partialShipments?: string;
  deliveryDate?: string | Dayjs;
  poNumber?: string;
  poDate?: string | Dayjs;
  invoiceNumber?: string;
  invoiceDate?: string | Dayjs;
  dispatchNumber?: string;
  dispatchDate?: string | Dayjs;
  awbNumber?: string;
  billOfLandingNumber?: string;
  deliveryNoteNumber?: string;
  deliveryNoteDate?: string | Dayjs | null;
  blNumber?: string;
  bulkDiscountRate?: number;
  totalBulkDiscount?: number;
  totalPenalty?: number;
  description?: string;
  enUser?: string;
  status?: string;
}

// Interface for hook return values
interface UseGrnDetailsReturn {
  warehouse: string;
  setWarehouse: React.Dispatch<React.SetStateAction<string>>;
  partialShipments: string;
  setPartialShipments: React.Dispatch<React.SetStateAction<string>>;
  deliveryDate: Dayjs | string;
  setDeliveryDate: React.Dispatch<React.SetStateAction<Dayjs | string>>;
  poNumber: string;
  setPoNumber: React.Dispatch<React.SetStateAction<string>>;
  poDate: Dayjs | string;
  setPoDate: React.Dispatch<React.SetStateAction<Dayjs | string>>;
  invoiceNumber: string;
  setInvoiceNumber: React.Dispatch<React.SetStateAction<string>>;
  invoiceDate: Dayjs | string;
  setInvoiceDate: React.Dispatch<React.SetStateAction<Dayjs | string>>;
  dispatchNumber: string;
  setDispatchNumber: React.Dispatch<React.SetStateAction<string>>;
  dispatchDate: Dayjs | string;
  setDispatchDate: React.Dispatch<React.SetStateAction<Dayjs | string>>;
  awbNumber: string;
  setAwbNumber: React.Dispatch<React.SetStateAction<string>>;
  billOfLandingNumber: string;
  setBillOfLandingNumber: React.Dispatch<React.SetStateAction<string>>;
  deliveryNoteNumber: string;
  setDeliveryNoteNumber: React.Dispatch<React.SetStateAction<string>>;
  deliveryNoteDate: string | null;
  setDeliveryNoteDate: React.Dispatch<React.SetStateAction<string | null>>;
  blNumber: string;
  setBlNumber: React.Dispatch<React.SetStateAction<string>>;
  bulkDiscountRate: number;
  setBulkDiscountRate: React.Dispatch<React.SetStateAction<number>>;
  totalBulkDiscount: number;
  setTotalBulkDiscount: React.Dispatch<React.SetStateAction<number>>;
  totalPenalty: number;
  setTotalPenalty: React.Dispatch<React.SetStateAction<number>>;
  description: string;
  setDescription: React.Dispatch<React.SetStateAction<string>>;
  enUser: string;
  setEnUser: React.Dispatch<React.SetStateAction<string>>;
  status: string;
  setStatus: React.Dispatch<React.SetStateAction<string>>;
  COMPCODE: string;
  STAFFMAPLOCODE: string;
  USERNAME: string;
  validateFields: () => Record<string, string>;
}

export function UseGrnDetails(initialData: GrnDetailsInitialData = {}): UseGrnDetailsReturn {
  // Get user parameters from Redux store (loose typing acceptable per plan)
  const SYS_USER = useSelector((state: AnyForNow) => state.user) || {};
  const { COMPCODE, STAFFMAPLOCODE, USERNAME } = SYS_USER.USER_PARAM || {};

  const [warehouse, setWarehouse] = useState<string>(initialData.warehouse || "");
  const [partialShipments, setPartialShipments] = useState<string>(
    initialData.partialShipments || ""
  );
  const [deliveryDate, setDeliveryDate] = useState<Dayjs | string>(
    dayjs(initialData.deliveryDate) || ""
  );
  const [poNumber, setPoNumber] = useState<string>(initialData.poNumber || "");
  const [poDate, setPoDate] = useState<Dayjs | string>(dayjs(initialData.poDate) || "");
  const [invoiceNumber, setInvoiceNumber] = useState<string>(
    initialData.invoiceNumber || ""
  );
  const [invoiceDate, setInvoiceDate] = useState<Dayjs | string>(
    dayjs(initialData.invoiceDate) || ""
  );
  const [dispatchNumber, setDispatchNumber] = useState<string>(
    initialData.dispatchNumber || ""
  );
  const [dispatchDate, setDispatchDate] = useState<Dayjs | string>(
    dayjs(initialData.dispatchDate) || ""
  );
  const [awbNumber, setAwbNumber] = useState<string>(initialData.awbNumber || "");
  const [billOfLandingNumber, setBillOfLandingNumber] = useState<string>(
    initialData.billOfLandingNumber || ""
  );
  const [deliveryNoteNumber, setDeliveryNoteNumber] = useState<string>(
    initialData.deliveryNoteNumber || ""
  );
  const [deliveryNoteDate, setDeliveryNoteDate] = useState<string | null>(
    (initialData.deliveryNoteDate as string) || null
  );
  const [blNumber, setBlNumber] = useState<string>(initialData.blNumber || "");
  const [bulkDiscountRate, setBulkDiscountRate] = useState<number>(
    initialData.bulkDiscountRate || 0
  );
  const [totalBulkDiscount, setTotalBulkDiscount] = useState<number>(
    initialData.totalBulkDiscount || 0
  );
  const [totalPenalty, setTotalPenalty] = useState<number>(
    initialData.totalPenalty || 0
  );
  const [description, setDescription] = useState<string>(initialData.description || "");
  const [enUser, setEnUser] = useState<string>(initialData.enUser || "");
  const [status, setStatus] = useState<string>(initialData.status || "A");

  const validateFields = useCallback((): Record<string, string> => {
    const newErrors: Record<string, string> = {};

    if (!warehouse) newErrors.warehouse = GRN_ERROR_MESSAGES.DELIVERY_LOCATION_REQUIRED;
    if (!deliveryDate) newErrors.deliveryDate = GRN_ERROR_MESSAGES.DELIVERY_DATE_REQUIRED;
    // if (!poNumber) newErrors.poNumber = "PO Number is required";
    if (!invoiceNumber) newErrors.invoiceNumber = GRN_ERROR_MESSAGES.INVOICE_NUMBER_REQUIRED;
    if (!invoiceDate) newErrors.invoiceDate = GRN_ERROR_MESSAGES.INVOICE_DATE_REQUIRED;
    if (!partialShipments)
      newErrors.partialShipments = GRN_ERROR_MESSAGES.PARTIAL_SHIPMENTS_REQUIRED;

    return newErrors;
  }, [warehouse, deliveryDate, invoiceNumber, invoiceDate, partialShipments]);

  return {
    warehouse,
    setWarehouse,
    partialShipments,
    setPartialShipments,
    deliveryDate,
    setDeliveryDate,
    poNumber,
    setPoNumber,
    poDate,
    setPoDate,
    invoiceNumber,
    setInvoiceNumber,
    invoiceDate,
    setInvoiceDate,
    dispatchNumber,
    setDispatchNumber,
    dispatchDate,
    setDispatchDate,
    awbNumber,
    setAwbNumber,
    billOfLandingNumber,
    setBillOfLandingNumber,
    deliveryNoteNumber,
    setDeliveryNoteNumber,
    deliveryNoteDate,
    setDeliveryNoteDate,
    blNumber,
    setBlNumber,
    bulkDiscountRate,
    setBulkDiscountRate,
    totalBulkDiscount,
    setTotalBulkDiscount,
    totalPenalty,
    setTotalPenalty,
    description,
    setDescription,
    enUser,
    setEnUser,
    status,
    setStatus,
    COMPCODE,
    STAFFMAPLOCODE,
    USERNAME,
    validateFields,
  };
}
